import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, type Trigger, type TriggerInput } from "../../api";
import { DICTS, useI18n, type Lang } from "../../lib/i18n";
import { useDialogs } from "../../components/Dialog";
import {
  Modal,
  ReadOnlyNote,
  Section,
  SitePicker,
  StatusLine,
  Switch,
  useSelectedSite,
  useSites,
  useStatus,
} from "./common";

/**
 * Messages the widget shows by itself: "Need help with setup?" after half a
 * minute on the setup page. Described in the words of what happens, not of
 * the event names underneath.
 */

const EMPTY: TriggerInput = {
  name: "",
  event: "time_on_page",
  delaySeconds: 30,
  urlPattern: null,
  message: "",
  onlyWhenOnline: true,
  enabled: true,
};

export function describeTrigger(
  rule: Pick<Trigger, "event" | "delaySeconds" | "urlPattern" | "onlyWhenOnline">,
  lang: Lang = "tr",
): string {
  const t = DICTS[lang].triggers;
  const when =
    rule.event === "time_on_page"
      ? t.whenTime(rule.delaySeconds)
      : rule.event === "exit_intent"
        ? t.whenExit
        : t.whenLoad(rule.delaySeconds);
  return t.describe(rule.urlPattern?.trim() || null, when, rule.onlyWhenOnline);
}

function Editor({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: TriggerInput;
  submitLabel: string;
  onSubmit: (input: TriggerInput) => Promise<void>;
  onCancel?: () => void;
}): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.triggers;
  const [d, setD] = useState(initial);
  const valid = d.name.trim().length > 0 && d.message.trim().length > 0;
  const events: Array<[Trigger["event"], string]> = [
    ["page_load", t.pageLoad],
    ["time_on_page", t.timeOnPage],
    ["exit_intent", t.exitIntent],
  ];
  return (
    <form
      class="grid-2"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit({
          ...d,
          name: d.name.trim(),
          message: d.message.trim(),
          urlPattern: d.urlPattern?.trim() || null,
        });
      }}
    >
      <label>
        {t.name} <span class="hint">{t.nameHint}</span>
        <input
          type="text"
          value={d.name}
          maxLength={80}
          onInput={(e) => setD({ ...d, name: e.currentTarget.value })}
          required
        />
      </label>
      <label>
        {t.when}
        <select value={d.event} onChange={(e) => setD({ ...d, event: e.currentTarget.value as Trigger["event"] })}>
          {events.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      {d.event !== "exit_intent" ? (
        <label>
          {t.delay}
          <input
            type="number"
            min={0}
            max={3600}
            value={d.delaySeconds}
            onInput={(e) =>
              setD({
                ...d,
                delaySeconds: Math.max(0, Math.min(3600, Number(e.currentTarget.value) || 0)),
              })
            }
          />
        </label>
      ) : null}
      <label>
        {t.pages} <span class="hint">{t.pagesHint}</span>
        <input
          type="text"
          placeholder={t.pagesExample}
          value={d.urlPattern ?? ""}
          maxLength={300}
          onInput={(e) => setD({ ...d, urlPattern: e.currentTarget.value })}
        />
      </label>
      <label class="span-2">
        {t.message}
        <textarea
          class="plain"
          value={d.message}
          maxLength={500}
          onInput={(e) => setD({ ...d, message: e.currentTarget.value })}
          required
        />
      </label>
      <div class="span-2">
        <Switch
          label={t.onlyOnline}
          hint={t.onlyOnlineHint}
          checked={d.onlyWhenOnline}
          onChange={(v) => setD({ ...d, onlyWhenOnline: v })}
        />
      </div>
      <p class="span-2 hint">{describeTrigger(d, lang)}</p>
      <div class="span-2 dialog-actions">
        {onCancel ? (
          <button type="button" class="btn" onClick={onCancel}>
            {all.common.cancel}
          </button>
        ) : null}
        <button class="btn primary" disabled={!valid}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

export function TriggersTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.triggers;
  const { sites } = useSites();
  const [site, selectSite] = useSelectedSite(sites);
  const [items, setItems] = useState<Trigger[]>([]);
  /** The rule being written or changed, in a window; "new" for a fresh one. */
  const [editor, setEditor] = useState<Trigger | "new" | null>(null);
  const [status, show, fail] = useStatus();
  const dialogs = useDialogs();

  const reload = useCallback(async () => {
    if (!site) return;
    try {
      setItems((await api.triggers(site.id)).triggers);
    } catch (err) {
      fail(err);
    }
  }, [site?.id, fail]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!site) return <div class="empty">{all.common.createSiteFirst}</div>;

  return (
    <>
      <ReadOnlyNote isAdmin={isAdmin} />
      <SitePicker sites={sites} value={site} onChange={selectSite} />
      <StatusLine status={status} />
      <Section
        title={t.listTitle}
        intro={t.listIntro}
        actions={
          isAdmin ? (
            <button class="btn primary" onClick={() => setEditor("new")}>
              + {t.newTitle}
            </button>
          ) : null
        }
      >
        {items.length === 0 ? <p class="hint">{t.none}</p> : null}
        <ul class="item-list">
          {items.map((item) => (
            <li key={item.id} class={item.enabled ? "" : "muted"}>
              <div class="item-main">
                <strong>{item.name}</strong>
                <span class="item-body">“{item.message}”</span>
                <span class="hint">{describeTrigger(item, lang)}</span>
              </div>
              {isAdmin ? (
                <div class="item-actions">
                  <label class="mini-switch">
                    <input
                      type="checkbox"
                      role="switch"
                      class="switch"
                      checked={item.enabled}
                      aria-label={t.toggle(item.name, item.enabled)}
                      onChange={(e) =>
                        void api
                          .updateTrigger(item.id, {
                            enabled: e.currentTarget.checked,
                          })
                          .then(reload)
                          .catch(fail)
                      }
                    />
                  </label>
                  <button class="btn sm" onClick={() => setEditor(item)}>
                    {all.common.edit}
                  </button>
                  <button
                    class="btn sm danger"
                    onClick={() => {
                      void dialogs
                        .confirm({
                          title: t.confirmDelete(item.name),
                          confirmLabel: all.common.delete,
                          danger: true,
                        })
                        .then((sure) => {
                          if (!sure) return;
                          return api.deleteTrigger(item.id).then(() => {
                            show("ok", t.deleted(item.name));
                            return reload();
                          });
                        })
                        .catch(fail);
                    }}
                  >
                    {all.common.delete}
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </Section>

      {editor ? (
        <Modal title={editor === "new" ? t.newTitle : editor.name} wide onClose={() => setEditor(null)}>
          <Editor
            initial={editor === "new" ? EMPTY : { ...editor }}
            submitLabel={editor === "new" ? all.common.add : all.common.save}
            onCancel={() => setEditor(null)}
            onSubmit={async (input) => {
              try {
                if (editor === "new") await api.createTrigger(site.id, input);
                else await api.updateTrigger(editor.id, input);
                show("ok", editor === "new" ? t.added(input.name) : t.saved(input.name));
                setEditor(null);
                await reload();
              } catch (err) {
                fail(err);
              }
            }}
          />
        </Modal>
      ) : null}
      {dialogs.dialog}
    </>
  );
}
